import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppUser, Listing } from '../../../types';
import { MailRecord, useListingEmails } from '../../../hooks/useListingEmails';
import { SectionTitle } from '../listing/formFields';
import EmailComposer from './EmailComposer';
import { formatDate } from './superFormat';

/** One listing's emails: write a new one to the owners, and the ones already sent (docs/email-plan.md). */
export default function EmailPanel({ listing, users }: { listing: Listing; users: AppUser[] }) {
  const { t } = useTranslation();
  const mails = useListingEmails(listing.id);
  // A new composer after each email, so the next one starts from the ready-made text again.
  const [round, setRound] = useState(0);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <SectionTitle>{t('directory.super.email.title')}</SectionTitle>
        <EmailComposer key={round} listing={listing} users={users} kind="message" onDone={() => setRound((r) => r + 1)} />
      </div>

      <div className="space-y-2">
        <SectionTitle>{t('directory.super.email.history')}</SectionTitle>
        {mails.length === 0 ? (
          <p className="text-sm text-ash">{t('directory.super.email.historyNone')}</p>
        ) : (
          <ul className="space-y-2">
            {mails.map((m) => (
              <SentEmail key={m.id} mail={m} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SentEmail({ mail }: { mail: MailRecord }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const failed = mail.status !== 'sent';

  return (
    <li className="rounded-[10px] bg-fog px-3 py-2 space-y-1">
      <p className="text-xs text-ash">
        {formatDate(mail.createdAt, i18n.language === 'he')}
        {' · '}
        {t(`directory.super.email.kind.${mail.kind}`)}
        {' · '}
        <span className={failed ? 'text-danger-600' : undefined}>{t(`directory.super.email.status.${mail.status}`)}</span>
      </p>
      <p className="text-sm text-ink break-words" dir="auto">{mail.subject}</p>
      <p className="text-xs text-ash break-all" dir="ltr">{mail.to.join(', ')}</p>
      <button type="button" onClick={() => setOpen((o) => !o)} className="min-h-[44px] text-sm text-ink hover:underline">
        {open ? t('directory.super.email.hideText') : t('directory.super.email.showText')}
      </button>
      {open && (
        <p className="text-sm text-ink whitespace-pre-line break-words" dir="auto">
          {mail.text}
        </p>
      )}
    </li>
  );
}
